import type { MediaCheckRow } from '../api/types';

/** PST-25: Instagram's image limits (the API's own copy lives in posts/domain/media-check.ts). */
const MIN_RATIO = 0.8;
const MAX_RATIO = 1.91;
const MIN_WIDTH = 320;
const MAX_WIDTH = 1440;

/** Source rectangle (centre crop) and destination size for one image. */
export interface OptimisePlan {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dw: number;
  dh: number;
}

/** PST-25: centre-crop to the nearest ratio in 4:5..1.91:1, then scale the width into 320..1440. */
export function optimisePlan(size: { width: number; height: number }): OptimisePlan {
  const { width, height } = size;
  const ratio = width / height;
  const sw = ratio > MAX_RATIO ? Math.round(height * MAX_RATIO) : width;
  const sh = ratio < MIN_RATIO ? Math.round(width / MIN_RATIO) : height;
  const dw = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, sw));
  return {
    sx: Math.round((width - sw) / 2),
    sy: Math.round((height - sh) / 2),
    sw,
    sh,
    dw,
    dh: Math.round((sh * dw) / sw),
  };
}

const FIXABLE = new Set(['format', 'aspect_ratio', 'low_resolution', 'oversized']);

/** An image whose size is known and whose issues the optimiser can fix. */
export const optimisable = (
  r: Pick<MediaCheckRow, 'type' | 'checked' | 'width' | 'height' | 'issues'>,
): boolean =>
  r.type === 'image' &&
  r.checked &&
  r.width !== null &&
  r.height !== null &&
  r.issues.some((i) => FIXABLE.has(i.code));

/** The line under a media url: size, duration, and "ok" when nothing is wrong. */
export function checkLabel(
  r: Pick<MediaCheckRow, 'checked' | 'width' | 'height' | 'durationMs' | 'issues'>,
): string {
  if (!r.checked) return 'not checked (hosted elsewhere; Instagram checks it on publish)';
  const parts = [
    ...(r.width !== null && r.height !== null ? [`${r.width} × ${r.height}`] : []),
    ...(r.durationMs !== null ? [`${(r.durationMs / 1000).toFixed(1)} s`] : []),
    ...(r.issues.length === 0 ? ['ok'] : []),
  ];
  return parts.join(' · ');
}

/** PST-25: draw the image through the plan onto a canvas and encode it as JPEG (quality 0.9). */
export async function optimiseImage(source: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  const plan = optimisePlan(bitmap);
  const canvas = document.createElement('canvas');
  canvas.width = plan.dw;
  canvas.height = plan.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('this browser cannot draw images');
  ctx.drawImage(bitmap, plan.sx, plan.sy, plan.sw, plan.sh, 0, 0, plan.dw, plan.dh);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('the image could not be encoded'))),
      'image/jpeg',
      0.9,
    ),
  );
}
